import { useState, useEffect, useCallback } from 'react';
import { FiTrendingUp, FiTrendingDown, FiDollarSign, FiCheckCircle, FiAlertTriangle } from 'react-icons/fi';
import API_URL from '../config';

const MMK = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
const today = () => new Date().toISOString().split('T')[0];

function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
}

const TYPE_LABEL = {
  SALE: 'Sale',
  PAYMENT: 'Payment',
  RESTOCK_COST: 'Restock',
  TRANSPORT: 'Transport',
};

export default function Ledger({ token }) {
  const [tab, setTab] = useState('ledger'); // 'ledger' | 'reconciliation'

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="text-lg font-semibold text-slate-800">Ledger &amp; Reconciliation</h2>
        <div className="flex bg-slate-100 rounded-lg p-1">
          {[['ledger', 'Ledger'], ['reconciliation', 'Reconciliation']].map(([id, label]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`px-3.5 py-1.5 rounded-md text-sm font-medium transition-colors ${
                tab === id ? 'bg-white shadow text-slate-800' : 'text-slate-500'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {tab === 'ledger' ? <LedgerTab token={token} /> : <ReconciliationTab token={token} />}
    </div>
  );
}

function LedgerTab({ token }) {
  const [start, setStart] = useState(daysAgo(30));
  const [end, setEnd] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchLedger = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_URL}/api/inventory/ledger?start=${start}&end=${end}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      if (res.ok) setData(d);
      else setError(d.error || 'Failed to load ledger.');
    } catch { setError('Cannot connect to server.'); }
    finally { setLoading(false); }
  }, [token, start, end]);

  useEffect(() => { fetchLedger(); }, [fetchLedger]);

  const s = data?.summary;

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <input type="date" value={start} onChange={e => setStart(e.target.value)}
          className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm" />
        <span className="text-slate-400 text-sm">to</span>
        <input type="date" value={end} onChange={e => setEnd(e.target.value)}
          className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm" />
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">{error}</div>
      )}

      {s && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
          <SummaryCard label="Cash in" value={s.cash_in} icon={FiTrendingUp} color="green" />
          <SummaryCard label="Cash out" value={s.cash_out} icon={FiTrendingDown} color="red" />
          <SummaryCard label="Net cash" value={s.net_cash} icon={FiDollarSign} color="blue" />
          <SummaryCard label="New credit sales" value={s.credit_sales_new} icon={FiTrendingUp} color="orange" />
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-2.5">Date</th>
              <th className="text-left px-4 py-2.5">Type</th>
              <th className="text-left px-4 py-2.5">Description</th>
              <th className="text-right px-4 py-2.5">Amount</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={4} className="text-center py-8 text-slate-400">Loading…</td></tr>
            )}
            {!loading && (!data?.entries || data.entries.length === 0) && (
              <tr><td colSpan={4} className="text-center py-8 text-slate-400">No ledger entries in this range.</td></tr>
            )}
            {data?.entries.map((e, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{e.date}</td>
                <td className="px-4 py-2.5 whitespace-nowrap">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    e.direction === 'IN' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
                  }`}>
                    {TYPE_LABEL[e.type] || e.type}
                  </span>
                  {!e.cash && <span className="ml-1.5 text-xs text-amber-600">· credit</span>}
                </td>
                <td className="px-4 py-2.5 text-slate-700">{e.description}</td>
                <td className={`px-4 py-2.5 text-right font-semibold tabular-nums whitespace-nowrap ${
                  e.direction === 'IN' ? 'text-emerald-700' : 'text-red-700'
                }`}>
                  {e.direction === 'IN' ? '+' : '−'} {MMK(e.amount)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, color }) {
  const colors = {
    green: 'bg-emerald-50 text-emerald-600',
    red: 'bg-red-50 text-red-600',
    blue: 'bg-blue-50 text-blue-600',
    orange: 'bg-orange-50 text-orange-600',
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className={`w-9 h-9 rounded-lg ${colors[color]} flex items-center justify-center mb-3`}>
        <Icon size={18} />
      </div>
      <p className="text-xl font-bold text-slate-800 tabular-nums">
        {MMK(value)} <span className="text-xs font-medium text-slate-400">MMK</span>
      </p>
      <p className="text-xs text-slate-500 mt-0.5">{label}</p>
    </div>
  );
}

function ReconciliationTab({ token }) {
  const [date, setDate] = useState(today());
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState('0');
  const [counted, setCounted] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);

  const fetchStatus = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_URL}/api/inventory/reconciliation?date=${date}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      if (res.ok) {
        setStatus(d);
        if (d.closed) { setOpening(String(d.opening_cash)); setCounted(String(d.counted_cash)); setNote(d.note || ''); }
        else { setOpening('0'); setCounted(''); setNote(''); }
      } else setError(d.error || 'Failed to load.');
    } catch { setError('Cannot connect to server.'); }
    finally { setLoading(false); }
  }, [token, date]);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/inventory/reconciliation/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      if (res.ok) setHistory(d);
    } catch { /* history is a nice-to-have, ignore errors */ }
  }, [token]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);
  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError('');
    try {
      const res = await fetch(`${API_URL}/api/inventory/reconciliation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          date,
          opening_cash: parseFloat(opening) || 0,
          counted_cash: parseFloat(counted) || 0,
          note,
        }),
      });
      const d = await res.json();
      if (res.ok) { setStatus(d); fetchHistory(); }
      else setError(d.error || 'Failed to save.');
    } catch { setError('Cannot connect to server.'); }
    finally { setSubmitting(false); }
  };

  const countedNum = parseFloat(counted) || 0;
  const expectedPreview = status && !status.closed ? (parseFloat(opening) || 0) + status.net_cash_movement : null;

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-800">Count cash</h3>
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
            className="border border-slate-200 rounded-lg px-2.5 py-1 text-sm" />
        </div>

        {loading ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : status?.closed ? (
          <div>
            <div className="flex items-center gap-2 mb-4 text-emerald-600 text-sm font-medium">
              <FiCheckCircle size={16} /> Closed by {status.closed_by || 'staff'}
            </div>
            <Row label="Opening cash" value={status.opening_cash} />
            <Row label="Expected cash" value={status.expected_cash} />
            <Row label="Counted cash" value={status.counted_cash} />
            <div className={`flex justify-between items-baseline mt-3 pt-3 border-t border-slate-100 ${
              Math.abs(status.variance) < 1 ? 'text-slate-800' : status.variance > 0 ? 'text-emerald-600' : 'text-red-600'
            }`}>
              <span className="font-semibold text-sm">Variance</span>
              <span className="font-bold tabular-nums">{status.variance > 0 ? '+' : ''}{MMK(status.variance)} MMK</span>
            </div>
            {status.note && <p className="text-xs text-slate-500 mt-3">Note: {status.note}</p>}
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500">Cash in today</p>
                <p className="font-bold text-emerald-600 tabular-nums">{MMK(status?.cash_in)}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500">Cash out today</p>
                <p className="font-bold text-red-600 tabular-nums">{MMK(status?.cash_out)}</p>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Opening cash (drawer float)</label>
              <input value={opening} onChange={e => setOpening(e.target.value.replace(/[^\d.]/g, ''))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Counted cash (actual in drawer)</label>
              <input value={counted} onChange={e => setCounted(e.target.value.replace(/[^\d.]/g, ''))} required
                placeholder="0" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            </div>
            {expectedPreview !== null && counted !== '' && (
              <div className={`text-sm font-medium ${
                Math.abs(countedNum - expectedPreview) < 1 ? 'text-slate-600' : countedNum > expectedPreview ? 'text-emerald-600' : 'text-red-600'
              }`}>
                Expected {MMK(expectedPreview)} · {countedNum >= expectedPreview ? 'over' : 'short'} by {MMK(Math.abs(countedNum - expectedPreview))}
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Note (optional)</label>
              <input value={note} onChange={e => setNote(e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            </div>
            {error && (
              <div className="flex items-center gap-2 text-red-600 text-sm"><FiAlertTriangle size={14} /> {error}</div>
            )}
            <button type="submit" disabled={submitting}
              className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium rounded-lg text-sm transition-colors">
              {submitting ? 'Closing…' : 'Close out day'}
            </button>
          </form>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="font-semibold text-slate-800 mb-4">History</h3>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {history.length === 0 && <p className="text-sm text-slate-400">No reconciliations yet.</p>}
          {history.map(h => (
            <div key={h.date} className="flex items-center justify-between text-sm py-2 border-b border-slate-100 last:border-0">
              <span className="text-slate-600">{h.date}</span>
              <span className={`font-semibold tabular-nums ${
                Math.abs(h.variance) < 1 ? 'text-slate-700' : h.variance > 0 ? 'text-emerald-600' : 'text-red-600'
              }`}>
                {h.variance > 0 ? '+' : ''}{MMK(h.variance)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-sm py-1.5">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium tabular-nums">{MMK(value)}</span>
    </div>
  );
}
